import { createContext, useContext, useEffect, useState, useMemo, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { clearSyncMetadata } from '@/lib/db';
import { clearMasterKeyForUser } from '@/lib/cryptoKeyStore';
import { isAndroidPlatform } from '@/utils/platform';
import { Browser } from '@capacitor/browser';
import { webRepository } from '@/lib/repository/webRepository';
import { logger } from '@/lib/logger';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, displayName?: string, englishVariant?: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper to send branded auth emails via edge function
async function sendBrandedEmail(
  email: string,
  type: 'password_reset' | 'welcome',
  options?: { redirectUrl?: string; displayName?: string }
): Promise<void> {
  try {
    const response = await supabase.functions.invoke('send-auth-email', {
      body: {
        email,
        type,
        redirectUrl: options?.redirectUrl,
        displayName: options?.displayName,
      },
    });

    if (response.error) {
      console.warn('[Auth] Failed to send email via edge function:', response.error);
    }
  } catch (err) {
    console.warn('[Auth] Edge function call failed:', err);
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  useEffect(() => {
    // Set initial session synchronously if present
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      if (session?.user?.id) {
        import('@/lib/revenuecat').then(({ initRevenueCat }) => {
          initRevenueCat(session.user.id);
        });
        if (isAndroidPlatform()) {
          import('@/lib/repository/androidRepository').then(({ androidRepository }) => {
            androidRepository.scanAndResumeOutbox();
          });
        }
      }
    });

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      logger.log('[Auth] State change:', event);
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      import('@/lib/revenuecat').then(({ syncRevenueCatUser }) => {
        syncRevenueCatUser(session?.user?.id ?? null);
      });

      if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session?.user?.id && isAndroidPlatform()) {
        import('@/lib/repository/androidRepository').then(({ androidRepository }) => {
          androidRepository.scanAndResumeOutbox();
        });
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signInWithGoogle = async () => {
    if (isAndroidPlatform()) {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: 'com.birchwrite.fiction://auth/callback',
          skipBrowserRedirect: true,
        },
      });
      if (error) throw error;
      if (data?.url) {
        await Browser.open({ url: data.url, windowName: '_self' });
      }
    } else {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/`,
        },
      });
      if (error) throw error;
    }
  };

  const signInWithEmail = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
  };

  const signUp = async (email: string, password: string, displayName?: string, englishVariant?: string) => {
    const variant = englishVariant || 'en-US';
    localStorage.setItem('deckle-english-variant', variant);
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
        data: {
          display_name: displayName,
          english_variant: variant,
        }
      }
    });
    if (error) throw error;

    // Send branded welcome email (supplements the Supabase confirmation email)
    await sendBrandedEmail(email, 'welcome', {
      redirectUrl: window.location.origin,
      displayName,
    });
  };

  const signOut = async () => {
    const currentUserId = user?.id;
    queryClient.clear();
    await clearMasterKeyForUser(currentUserId).catch((err) => console.warn('[Auth] Failed to clear master key:', err));
    if (isAndroidPlatform()) {
      clearSyncMetadata();
    } else {
      webRepository.clearCacheOnAuthChange();
    }
    const { error } = await supabase.auth.signOut({ scope: 'local' });
    if (error) throw error;
  };

  const resetPassword = async (email: string) => {
    const redirectUrl = `${window.location.origin}/auth?mode=reset`;
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    if (error) throw error;

    // Send branded password reset email (this replaces the need for the default email)
    await sendBrandedEmail(email, 'password_reset', {
      redirectUrl,
    });
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    if (error) throw error;
  };

  const contextValue = useMemo(() => ({
    user,
    session,
    loading,
    signInWithGoogle,
    signInWithEmail,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
  }), [user, session, loading]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
